import type { BrowserHistory, Location, Update } from '@remix-run/router/history';
import type { IHistoryEvent, IHistoryTracking, IHistoryTransitionType } from './interface';

function LocationHistory(history: BrowserHistory | undefined): IHistoryTracking {
    const stack: Readonly<Location>[] = [];
    let lastEvent: Readonly<IHistoryEvent> | undefined;

    history?.listen((update: Update) => {
        lastEvent = Object.freeze({
            action: update.action,
            pathname: update.location.pathname,
            search: update.location.search,
            hash: update.location.hash,
        });

        const actions: Record<IHistoryTransitionType, () => void> = {
            PUSH: () => {
                stack.push(Object.freeze(update.location));
            },
            POP: () => {
                stack.splice(-1, 1);
            },
            REPLACE: () => {
                stack.splice(-1, 1, update.location);
            },
        };

        actions[update.action]();
    });

    function _findPathPrefixIndex(locations: Location[], pathPrefix: string): number {
        for (const location of locations) {
            if (location.pathname.startsWith(pathPrefix)) {
                return locations.indexOf(location);
            }
        }
        return -1;
    }

    return {
        length: () => stack.length,
        history: () => Object.freeze(stack),
        lastEvent: () => lastEvent,
        previousLocation: () => stack.at(-1),
        findFirstPathPrefix: function (pathPrefixes: string[]) {
            for (const [index, path] of pathPrefixes.entries()) {
                const indexSearch = _findPathPrefixIndex(stack, path);
                if (indexSearch >= 0) {
                    return index;
                }
            }
            return -1;
        },
        findPathPrefix: (pathPrefix: string) => _findPathPrefixIndex(stack, pathPrefix),
    };
}

export default LocationHistory;
